<template>
    <feature v-slot="{ id }" feature-key="interim" message-if-no-access sub-key="page">
        <div :id="id" class="w-full xl:w-5/6 max-w-960 mx-auto relative">

            <header class="mb-24">
                <div class="flex justify-between">
                    <h2 class="font-semibold text-heading-2">
                        {{ $t('interims.title') }}
                    </h2>

                    <more-info-button>
                        <template #content>
                            <b class="block mb-4">{{ $t('general.more_info') }}</b>
                            <p v-html="$t('interim.interim_info', {'hover': 'hover:text-primary'})"></p>
                        </template>
                    </more-info-button>
                </div>

                <tabs
                    :active-key="activeItem"
                    :items="tabs"
                    class="mt-6"
                    @set-active="activeItem = $event"
                >
                </tabs>
            </header>


            <div v-if="can.manage" class="flex items-center mb-16">
                <div class="inline-flex mr-12">
                    <double-input
                        button
                        @click="startAddingInterim"
                    >
                        <template #default>
                            <div class="flex items-center gap-8">
                                <svg-icon
                                    type="add-circle"
                                    class="w-18 h-18 fill-secondary"
                                />
                                <p class="first-letter:uppercase">{{ $t('interims.add') }}</p>
                            </div>
                        </template>
                        <template #button>
                            <simple-dropdown class="h-full">
                                <template #trigger="{isOpen}">
                                    <div class="px-8 h-full flex items-center">
                                        <svg-icon :type="isOpen ? 'keyboard-arrow-up' : 'keyboard-arrow-down'" />
                                    </div>
                                </template>
                                <template #content="{ close }">
                                    <nav
                                        aria-orientation="vertical"
                                        class="bg-white relative rounded shadow"
                                        role="menu"
                                    >
                                        <dropdown-item
                                            icon="add-circle"
                                            :input-label="$t('interims.add_section')"
                                            @click="close(); startAddingSection()"
                                        />
                                    </nav>
                                </template>
                            </simple-dropdown>
                        </template>
                    </double-input>
                </div>

                <simple-dropdown>
                    <template #trigger="{isOpen}">
                        <action-button
                            :icon="isOpen ? 'keyboard-arrow-up' : 'keyboard-arrow-down'"
                        >
                            {{ activeItem === 'interim' ? $t('interims.interim_office') : $t('interims.selection_office') }}
                        </action-button>
                    </template>
                    <template #content="{ close }">
                        <nav
                            aria-orientation="vertical"
                            class="bg-white relative rounded shadow"
                            role="menu"
                        >
                            <dropdown-item
                                icon="keyboard-arrow-right"
                                :input-label="$t('interims.show_matrix')"
                                @click="close(); startShowingMatrix()"
                            />
                            <dropdown-item
                                icon="edit"
                                :input-label="$t('interims.edit_matrix')"
                                @click="close(); startEditingMatrixTypes()"
                            />
                        </nav>
                    </template>
                </simple-dropdown>
            </div>


            <loading v-if="!interimsModuleStore.allInterimsFetched"/>
            <template v-else>
                <!-- interim with sections -->
                <div v-if="sections.length"
                     class="mb-24">
                    <loading v-if="addingSection"/>

                    <draggable
                        v-bind="dragOptions"
                        v-model="sections"
                        handle=".draggable-section"
                        item-key="id"
                        class="border-b border-primary-20 pb-24 mb-24 space-y-16"
                        @end="updateSection"
                        @start="drag = true"
                    >
                        <template #item="{ element }">
                            <div
                                :id="'section-'+element.id"
                                class="draggable-section"
                                @mousedown="currentSection = element"
                            >
                                <div class="flex items-center gap-8 cursor-pointer">
                                    <div class="cursor-grab">
                                        <svg-icon class="w-18 h-18 text-primary-80 hover:text-primary" type="drag-indicator" />
                                    </div>

                                    <div class="flex items-center">
                                        <click-edit-input
                                            :ref="'edit-' + element.id"
                                            :value="element.name"
                                            @input="startEditingSection($event, element.id)"
                                        />

                                        <button
                                            class="inline-flex items-center fill-primary"
                                            role="button"
                                            @click.prevent="showHideSection(element)"
                                        >
                                            <svg-icon :class="{'-rotate-90': !element.visible}" class="w-18 h-18 transition-transform" type="keyboard-arrow-down"/>
                                        </button>
                                    </div>

                                    <div class="flex gap-8">
                                        <button
                                            :title="$t('interims.form.title')"
                                            @click="startAddingInterim(element)"
                                        >
                                            <svg-icon class="max-w-12 max-h-12" type="add"/>
                                        </button>

                                        <simple-dropdown position="right">
                                            <template #trigger>
                                                <button>
                                                    <svg-icon class="w-14 h-14" type="more-horiz"/>
                                                </button>
                                            </template>
                                            <template #content="{ close }">
                                                <nav
                                                    aria-orientation="vertical"
                                                    class="bg-white relative rounded shadow"
                                                >
                                                    <dropdown-item
                                                        icon="edit"
                                                        :input-label="$t('interims.rename_section')"
                                                        @click="close(); $refs['edit-' + element.id].startEdit()"
                                                    />
                                                    <dropdown-item
                                                        danger
                                                        icon="delete"
                                                        :input-label="$t('interims.delete_section')"
                                                        @click="close(); startDeletingSection(element)"
                                                    />
                                                </nav>
                                            </template>
                                        </simple-dropdown>
                                    </div>
                                </div>


                                <div :id="'section-'+element.id"
                                     :class="element.visible ? '' : 'hidden'"
                                     class="mt-8">

                                    <draggable-interim
                                        v-model="activeItem"
                                        :drag="drag"
                                        :highlight="highlight"
                                        :sectionId="element.id"
                                        @move="checkMove"
                                        @start="drag = true"
                                        @end="drag = false"
                                    ></draggable-interim>
                                </div>
                            </div>
                        </template>
                    </draggable>
                </div>

                <!-- interims without section -->
                <draggable-interim
                    v-model="activeItem"
                    :drag="drag"
                    class="mb-32"
                    @move="checkMove"
                    @start="drag = true"
                />

                <empty-state v-if="!interims.length"
                             :button-text="$t('interims.add')"
                             :description="$t('interims.empty_state.description')"
                             :title="$t('interims.empty_state.title')"
                             button-icon="add-circle"
                             @click="startAddingInterim"
                >
                </empty-state>
            </template>


            <!-- Popup Add/Edit interim -->
            <add-interim v-if="addingInterim" :active-item="activeItem" :section="addingInterimSection" @close="stopAddingInterim"/>

            <!-- Popup delete section -->
            <popup-modal-confirm
                v-if="deletingSection"
                :message="$t('interims.confirm_deletion_section.message', {subject: deletingSection.name, amount: interimInSection})"
                show-exclamation
                :title="$t('interims.confirm_deletion_section.title')"
                :working="performingDeleteSectionRequest"
                @close="stopDeletingSection"
                @confirm="confirmSectionDeletion"
            />

            <!-- Popup edit matrix types -->
            <edit-interim-matrix-types v-if="editingMatrixTypes" :active-item="activeItem" @close="stopEditingMatrixTypes"/>

            <!-- Popup show all matrixes -->
            <popup-modal
                position="center"
                v-if="showingMatrix"
                without-padding
                @close="stopShowingMatrix"
            >
                <interim-matrix :interims="interims" class="w-484 md:w-764 overflow-auto p-24 pt-44"/>
            </popup-modal>
        </div>
    </feature>
</template>

<script>
import SimpleButton from '../components/ui/SimpleButton';
import SvgIcon from '../components/ui/SvgIcon';
import * as interims from '../services/InterimService';
import * as interimSections from '../services/InterimSectionService';
import Interim from '../models/Interim';
import {normalizeJson} from '../../shared/helpers';
import Loading from '../components/ui/Loading';
import AddInterim from '../components/ui/AddInterim';
import SimpleDropdown from '../components/ui/SimpleDropdown';
import PopupModalConfirm from '../components/ui/PopupModalConfirm';
import RoundButton from '../components/ui/RoundButton';
import EditInterimMatrixTypes from '../components/EditInterimMatrixTypes';
import PopupModal from '../components/ui/PopupModal';
import InterimMatrix from '../components/InterimMatrix';
import Feature from '../components/Feature';
import Tooltip from '../components/navigation/Tooltip.vue';
import draggable from 'vuedraggable';
import {ADD_NOTIFICATION} from '../state/mutation-types';
import InterimSection from '../models/InterimSection';
import ClickEditInput from '../components/forms/ClickEditInput';
import {mapActions, mapStores} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../state/store';
import {useInterimsStore} from '../state/modules/interims';
import {useNotificationsStore} from '../state/modules/notifications';
import InterimList from '../components/InterimList.vue';
import Tabs from '../components/navigation/Tabs.vue';
import DraggableInterim from '../components/DraggableInterim.vue';
import EmptyState from '../components/ui/EmptyState.vue';
import MoreInfoButton from '../components/ui/MoreInfoButton.vue';
import DropdownItem from "../components/ui/DropdownItem.vue";
import ActionButton from "../components/ui/ActionButton.vue";
import DoubleInput from "../components/forms/DoubleInput.vue";

export default {
    name: 'Interims',
    components: {
        DoubleInput,
        ActionButton,
        DropdownItem,
        MoreInfoButton,
        EmptyState,
        DraggableInterim,
        Tabs,
        InterimList,
        ClickEditInput,
        Feature,
        PopupModal,
        EditInterimMatrixTypes, InterimMatrix,
        RoundButton, PopupModalConfirm, SimpleDropdown, AddInterim, Loading, SimpleButton, SvgIcon,
        Tooltip,
        draggable,
    },
    data() {
        return {
            // fetchingInterims: false,
            addingInterim: false,
            addingInterimSection: Object,

            interimInSection: 0,
            deletingSection: false,
            performingDeleteSectionRequest: false,

            editingMatrixTypes: false,
            showingMatrix: false,

            actionButtons: null,
            addingSection: false,
            drag: false,
            currentInterim: null,
            currentSection: null,
            highlight: null,

            activeItem: 'interim',
        };
    },
    computed: {
        ...mapStores(useInterimsStore),
        can() {
            return {
                view: this.$can('interims.view'),
                manage: this.$can('interims.manage'),
            };
        },
        tabs() {
            return {
                'interim': {
                    'title': this.$t('interims.interim_office'),
                    'icon': '',
                },
                'selection': {
                    'title': this.$t('interims.selection_office'),
                    'icon': '',
                },
            };
        },
        interims() {
            return useRepo(Interim)
                .with('matrixValues', (q) => {
                    q.with('type')
                })
                .orderBy('order')
                .where('type', this.activeItem)
                .get();
        },
        sections: {
            get() {
                return useRepo(InterimSection)
                    .orderBy('order')
                    .get();
            },
            set(sections) {
                this.resortSection(sections);
            },
        },
        dragOptions() {
            return {
                animation: 200,
                group: 'sections',
                disabled: false,
                ghostClass: 'ghost',
            };
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        ...mapActions(useMainStore, ['setDocumentTitle']),
        fetch() {
            this.fetchingInterims = true;

            this.interimsModuleStore.startFetching();

            Promise.all([
                interims.index().then(results => {
                    return useRepo(Interim).save(normalizeJson(results.data));
                }),
                interimSections.index().then(results => {
                    return useRepo(InterimSection).save(normalizeJson(results.data));
                })],
            ).then(() => {
                this.interimsModuleStore.endFetching();
                this.interimsModuleStore.setAllInterimsFetched();

                this.fetchingInterims = false;
            });
        },
        startAddingInterim(section) {
            stopHsTracking();

            this.addingInterim = true;
            this.addingInterimSection = ((section && section.constructor.entity === 'interim-sections') ? section : '');
        },
        stopAddingInterim() {
            startHsTracking();
            this.addingInterim = false;
        },
        stopDeletingSection() {
            this.deletingSection = false;
        },
        confirmSectionDeletion() {
            this.performingDeleteSectionRequest = true;

            //Remove section_id from interims
            useRepo(Interim)
                .where('section_id', this.deletingSection.id)
                .update({ section_id: null });

            for (let interim of this.interims) {
                if (interim.section_id === this.deletingSection.id) {
                    interims.update(
                        interim.id,
                        {data: {attributes: {section_id: null}}},
                    );
                }
            }

            //Delete section
            this.deleteSection(this.deletingSection);
        },
        startEditingMatrixTypes() {
            return this.editingMatrixTypes = true;
        },
        stopEditingMatrixTypes() {
            return this.editingMatrixTypes = false;
        },
        startShowingMatrix() {
            return this.showingMatrix = true;
        },
        stopShowingMatrix() {
            return this.showingMatrix = false;
        },
        talkToUs() {
            window.HubSpotConversations.widget.open();
        },

        startAddingSection() {
            this.addingSection = true;

            return interimSections.store({
                data: {
                    attributes: {
                        name: this.$t('interims.untitled_section'),
                    },
                },
            }).then(result => {
                this.addingSection = false;

                return useRepo(InterimSection).save(normalizeJson(result));
            });
        },
        startEditingSection(event, sectionId) {
            // Update the section in our store to change it immediately.
            useRepo(InterimSection)
                .whereId(sectionId)
                .update({ name: event });

            // Update name in db
            interimSections.update(sectionId, {data: {attributes: {name: event}}});
        },
        startDeletingSection(section) {
            //Check whether the section contains interims
            let amount = this.interims.filter(interim => interim.section_id === section.id).length;

            if (amount && amount > 0) {
                //open popup for delete section
                this.deletingSection = section;
                this.interimInSection = amount;
            } else {
                //No need for popup if there are no items for that section
                this.deleteSection(section);
            }
        },
        deleteSection(section) {
            interimSections.destroy(section.id)
                .then(() => {
                    return useRepo(InterimSection).destroy(section.id);
                }).then(() => {
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('general.deleted', {subject: section.name}),
                });
            }).finally(() => {
                this.performingDeleteSectionRequest = false;
                this.deletingSection = false;
                this.interimInSection = 0;
            });
        },
        resortSection(sortedSection) {
            // //Reverse the new sorted sections order
            // let reverseArr = sortedSection.map((item, index) => sortedSection[sortedSection.length - 1 - index]);
            //
            // //Change order in reversed arr
            // let sort = reverseArr.map((section, index) => ({id: section.id, order: index}));

            //When dragging an interim in a section, resortSection is triggered.
            //Filter out to only get interim-section modal
            let sort = sortedSection.filter(section => section.constructor.entity === 'interim-sections')
                .map((section, index) => ({id: section.id, order: index}));

            useRepo(InterimSection).whereIn('id', sort.map(sort => sort.id))
                .get()
                .map(section => ({
                    id: section.id,
                    order: section.order,
                }));

            useRepo(InterimSection).save(sort);
        },
        updateSection(evt) {
            if (evt.newDraggableIndex === this.currentSection.order) {
                this.currentSection = null;
                this.drag = false;
                return;
            }

            interimSections.update(
                this.currentSection.id,
                {data: {attributes: {order: evt.newDraggableIndex}}},
            ).then(() => {
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('general.order_changed', {subject: 'section'}),
                });
            });

            this.currentSection = null;
            this.drag = false;
        },
        checkMove(evt) {
            //When moving an interim check if you are moving it to an fake element to hide the fake element
            let fakeId = evt.to.children[0].id;

            if (fakeId.includes('fake')) {
                this.highlight = parseInt(fakeId.replace('fake-', ''));
            } else {
                this.highlight = null;
            }
        },
        showHideSection(section) {
            const rows = useRepo(InterimSection)
                .whereId(section.id)
                .get()
                .map((e) => {
                    return { id: e.id, visible: !e.visible }
                });

            useRepo(InterimSection).save(rows);
        },
    },
    mounted() {
        this.fetch();

        this.setDocumentTitle(this.$t('interims.title'));
    },
};
</script>
